// @ts-check

import { DICTIONARIES_IN_API, PAGES } from "@consts";

/**
 *
 * @param {WF_Common.RouteObject} route
 * @param {WF_ServiceEndpointDictionary.Module} serviceEndpointProvider
 * @returns
 */
export const getEndpointCallerForRoute = (route, serviceEndpointProvider) => {
	switch (route.name) {
		case PAGES.SCRABBLE_DICT_RESULTS:
		case PAGES.NYT_CROSSPLAY_DICTIONARY_RESULTS:
		case PAGES.UNSCRAMBLER_RESULTS: {
			return (params, override) => {
				const selectedDict =
					params.dictionary || params?.misc?.dictionary || "US";
				const unscramblerExtraParams =
					route.name === PAGES.UNSCRAMBLER_RESULTS
						? {
								starts: params.advanced?.starts || undefined,
								ends: params.advanced?.ends || undefined,
								contains:
									params.advanced?.contains || undefined,
								word_length:
									params.advanced?.length || undefined,
						  }
						: {};

				return serviceEndpointProvider.callUnscramblerEndpoint({
					// These properties will come from the URL, and thus from the params object.
					tiles: params.tiles || "",
					dictionary: selectedDict,
					original_tiles: params.tiles || "",
					order_by: params.order_by || "score",

					// These properties don't show up in the URL, and can be overridden directly in the API call.
					offset: 0,
					limit: 35,
					...unscramblerExtraParams,
					...override,

					// These properties will never be overriden
					dictionary_opt: "YDR",
					augment_results: "min_5_add_1",
					group_by: "word_length",
					check_exact_match: true,
					exclude_original: ["WWF", "US", "UK"].includes(
						selectedDict
					),
					bonus: selectedDict === DICTIONARIES_IN_API.CP,
				});
			};
		}

		case PAGES.ANAGRAM_RESULTS:
			return (params, override) =>
				serviceEndpointProvider.callUnscramblerEndpoint({
					// These properties will come from the URL, and thus from the params object.
					tiles: params.tiles || "",
					order_by: params.order_by || "alpha",
					original_tiles: params.tiles || "",

					// These properties don't show up in the URL, and can be overridden directly in the API call.
					offset: 0,
					limit: 35,
					...override,

					// These properties will never be overriden
					dictionary_opt: "YDR",
					exclude_original: true,
					group_by: "word_length",
					dictionary: "ANAGRAM",
				});

		case PAGES.WORDLE_RESULTS:
		case PAGES.QUORDLE_RESULTS:
			return (params, override) =>
				serviceEndpointProvider.callWordlistEndpoint({
					// These properties will come from the URL, and thus from the params object.
					order_by: params.order_by || "alpha",
					correct_tiles: params.wordle?.correct || "",
					includes: params.wordle?.includes || "",
					excludes: params.wordle?.excludes || "",

					// These properties don't show up in the URL, and can be overridden directly in the API call.
					offset: 0,
					limit: 35,
					...override,

					// These properties will never be overriden
					suggest_links: true,
					group_by: "word_length",
					has_definition: "check",
					dictionary:
						route.name === PAGES.WORDLE_RESULTS
							? "WORDLE_ALL"
							: "WORDLE",
					word_length: "5",
				});

		case PAGES.WORDS_STARTING_RESULTS:
		case PAGES.WORDS_ENDING_RESULTS:
		case PAGES.WORDS_HAVING_RESULTS:
		case PAGES.WORDS_BY_LENGTH_RESULTS:
		case PAGES.WORDS_COMBINATION_LETTERS_RESULTS:
		case PAGES.WORDS_COMBINATION_LENGTH_AND_LETTERS_RESULTS: {
			return (params, override) => {
				let special;
				let limit = 35;
				let offset;
				const isLengthPage =
					route.name === PAGES.WORDS_BY_LENGTH_RESULTS ||
					route.name ===
						PAGES.WORDS_COMBINATION_LENGTH_AND_LETTERS_RESULTS;

				if (isLengthPage) {
					const page = params.misc?.page || 1;
					special = { special: "length" };
					limit =
						params.advanced?.length === 4 ||
						params.advanced?.length === 6 ||
						params.advanced?.length === 7
							? 250
							: 100;
					offset = page > 1 && limit * page - limit;
				}

				const interlinks = [];

				let contains = params.advanced?.contains || undefined;

				const isLetterAndLetterPage =
					route.name === PAGES.WORDS_HAVING_RESULTS &&
					/^\w+-and-\w+$/.test(contains);

				if (isLetterAndLetterPage) {
					special = { special: contains };
					contains = undefined;
				}

				if (params.advanced?.starts) {
					interlinks.push("starts");
				}
				if (params.advanced?.ends) {
					interlinks.push("ends");
				}
				if (params.advanced?.contains && !isLetterAndLetterPage) {
					interlinks.push("contains");
				}
				if (!isNaN(parseInt(params.advanced?.length))) {
					interlinks.push("length");
				}

				return serviceEndpointProvider.callWordlistEndpoint({
					// These properties will come from the URL, and thus from the params object.
					order_by: params.order_by || "score",
					dictionary:
						params.dictionary || params?.misc?.dictionary || "US",
					starts: params.advanced?.starts || undefined,
					contains,
					ends: params.advanced?.ends || undefined,
					word_length: params.advanced?.length || undefined,

					// These properties don't show up in the URL, and can be overridden directly in the API call.
					limit,
					offset: offset || 0,
					...override,

					// These properties will never be overriden
					suggest_links: true,
					group_by: "word_length",
					has_definition: "check",
					interlink_type: interlinks.join(",") || undefined,
					...special,
				});
			};
		}

		case PAGES.WORDS_ENDING_ING:
		case PAGES.WORDS_WITH_Q_NO_U:
		case PAGES.WORDS_VOWELS:
		case PAGES.WORDS_CONSONANTS: {
			const specialMap = {
				[PAGES.WORDS_ENDING_ING]: "ing",
				[PAGES.WORDS_WITH_Q_NO_U]: "q-no-u",
				[PAGES.WORDS_VOWELS]: "vowels",
				[PAGES.WORDS_CONSONANTS]: "consonants",
			};

			return (params, override) => {
				return serviceEndpointProvider.callWordlistEndpoint({
					// These properties will come from the URL, and thus from the params object.
					order_by: params.order_by || "score",
					dictionary:
						params.dictionary || params?.misc?.dictionary || "WWF",

					// These properties don't show up in the URL, and can be overridden directly in the API call.
					limit: 35,
					offset: 0,
					...override,

					// These properties will never be overriden
					suggest_links: true,
					group_by: "word_length",
					has_definition: "check",
					special: specialMap[route.name],
				});
			};
		}
	}

	console.error("+++ endpoint-caller-for-route: route not matched: ", route);
};
